import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import WorldLoreModal from "./WorldLoreModal";

// Order per Founder's spec — Aethelgard + Soljins first (LIVE), then the
// requested cadence, tail = Far Dark, McPirl, Waking Maw, Convergence.
const DOORS = [
  { slug: "aethelgard", name: "Aethelgard", tag: "The Waking World",
    image: "/art/aethelgard_hero.webp", domain: "https://aethelgardthelivingworlds.com",
    status: "LIVE", font: "'Cormorant Garamond', serif", color: "#FF5A00" },
  { slug: "soljins", name: "Soljins", tag: "The Tale of Time",
    image: "/art/worlds/world_7.jpg", domain: "https://soljins.com",
    status: "COMING", font: "'Cinzel', serif", color: "#3DE0C8" },
  { slug: "neon-requiem", name: "Neon Requiem", tag: "Cyberpunk Noir",
    image: "/art/worlds/world_0.jpg", domain: "https://neonrequiem.com",
    status: "COMING", font: "'Chakra Petch', sans-serif", color: "#FF4FD8" },
  { slug: "gilded-cage", name: "The Gilded Cage", tag: "Dystopian Rebellion",
    image: "/art/worlds/world_1.jpg", domain: "https://thegildedcageresilience.com",
    status: "COMING", font: "'Playfair Display', serif", color: "#E8C36A" },
  { slug: "hostile-takeover", name: "Hostile Takeover", tag: "Corporate Reckoning",
    image: "/art/worlds/world_5.jpg", domain: "https://hostiletakeoverwtf.com",
    status: "COMING", font: "'Bebas Neue', sans-serif", color: "#F0554E" },
  { slug: "sundered-weave", name: "The Nine Threads", tag: "High Fantasy Epic",
    image: "/art/worlds/world_2.jpg", domain: "https://sunderedweave.com",
    status: "COMING", font: "'Cinzel', serif", color: "#F0C64B" },
  { slug: "first-fire", name: "First Fire", tag: "Before the Ice",
    image: "/art/worlds/world_3.jpg", domain: "https://firstfireresilience.com",
    status: "COMING", font: "'IM Fell English SC', serif", color: "#FF7A45" },
  { slug: "last-lighthouse", name: "The Last Lighthouse", tag: "Dying-Universe Opera",
    image: "/art/worlds/world_4.jpg", domain: "https://lastlighthouse.com",
    status: "COMING", font: "'Cormorant Garamond', serif", color: "#A9D3F0" },
  { slug: "whodunnit", name: "Whodunnit", tag: "Eleven at the Start",
    image: "/art/worlds/world_6.jpg", domain: "https://whodunnit11.com",
    status: "COMING", font: "'Playfair Display', serif", color: "#D66B7E" },
  { slug: "far-dark", name: "The Far Dark", tag: "Deep Space",
    image: "/art/worlds/world_9.jpg", domain: "https://thefardark.com",
    status: "COMING", font: "'Orbitron', sans-serif", color: "#9CC0F5" },
  { slug: "mcpirl", name: "McPirl", tag: "A True Legend, Replayed",
    image: "/art/worlds/world_10.jpg", domain: "https://mcpirl.com",
    status: "COMING", font: "'Bebas Neue', sans-serif", color: "#A5D66A" },
  { slug: "waking-maw", name: "The Waking Maw", tag: "Eldritch · 18+",
    image: "/art/worlds/world_8.jpg", domain: "https://thewakingmaw.com",
    status: "COMING", font: "'IM Fell English SC', serif", color: "#C0392B", mature: true },
  { slug: "convergence", name: "The Convergence", tag: "The Last Door",
    image: "/art/worlds/world_11.jpg", domain: "https://convergencethe12thworld.com",
    status: "COMING", font: "'Cinzel Decorative', serif", color: "#ECD08A" },
];

function Badge({ status, color }) {
  if (status === "LIVE")
    return (
      <span
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full border ember-pulse"
        style={{ color, borderColor: `${color}66`, backgroundColor: `${color}18` }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
        Live now
      </span>
    );
  return (
    <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] px-3 py-1.5 rounded-full border border-white/15 text-[#a1a1aa]">
      Coming soon
    </span>
  );
}

function Door({ d, i, onOpen }) {
  return (
    <motion.button
      type="button"
      onClick={() => onOpen(d)}
      data-testid={`door-${d.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.75, delay: (i % 3) * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="group relative block w-full text-left overflow-hidden border border-white/[0.08] hover:border-[color:var(--door-accent)]/50 transition-colors duration-500 cursor-pointer"
      style={{ "--door-accent": d.color }}
      aria-label={`Read the lore treatment for ${d.name}`}
    >
      <div className="relative w-full h-[48vh] min-h-[320px] max-h-[560px] overflow-hidden">
        <div
          className="absolute inset-0 bg-center bg-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
          style={{ backgroundImage: `url(${d.image})` }}
          role="img"
          aria-label={d.name}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/45 to-black/92 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{ background: `radial-gradient(circle at 50% 100%, ${d.color}2a, transparent 55%)` }}
        />
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 sm:pb-16 px-6 text-center">
        <p
          className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.4em] mb-3"
          style={{ color: d.color }}
        >
          {d.tag}
          {d.mature && (
            <span className="ml-3 font-mono text-[9px] font-bold text-[#C0392B] border border-[#C0392B]/60 rounded px-1.5 py-0.5 bg-[#C0392B]/10">
              18+
            </span>
          )}
        </p>
        <h3
          className="uppercase leading-none drop-shadow-[0_2px_28px_rgba(0,0,0,0.85)]"
          style={{
            fontFamily: d.font,
            color: "#f5f0e8",
            fontSize: "clamp(2.5rem, 6vw, 5.5rem)",
            letterSpacing: "0.015em",
          }}
        >
          {d.name}
        </h3>
        <div className="mt-6 flex items-center gap-3">
          <Badge status={d.status} color={d.color} />
          <span
            className="font-mono text-[10px] uppercase tracking-[0.32em] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 flex items-center gap-1.5"
            style={{ color: d.color }}
          >
            Read the treatment <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </motion.button>
  );
}

export default function WorldDoorsStack() {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="doors"
      data-testid="world-doors-stack"
      className="relative w-full py-16 sm:py-24 px-6 sm:px-10 max-w-[1600px] mx-auto"
    >
      <div className="mb-12 sm:mb-16">
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#ff5a00] mb-4">
          The Doors
        </p>
        <h2 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-[#ededed] leading-none">
          Thirteen doors. Click any door · read the treatment.
        </h2>
      </div>
      <div className="flex flex-col gap-4 sm:gap-6">
        {DOORS.map((d, i) => (
          <Door key={d.slug} d={d} i={i} onOpen={setOpen} />
        ))}
      </div>
      {open && (
        <WorldLoreModal
          slug={open.slug}
          name={open.name}
          domain={open.domain}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}
